import * as constants from 'root-config/constants.js'
import TravelProtectionPlanType from 'root-packages/travelProtection/index.js'

const tabStepNumberMap = {'tour-overview': '1', 'extras': '2','passengers': '3','payment': '4', 'confirmation': '5'}

function snapshotUser (app) {

  function snapshotTravelAgent (app) {
    return {
      type: 'TA',
      agency_name: `${app.user.organisation}`,
      status: 'repeat',
      email: app.user.email,
    }
  }

  function snapshotCustomer (app) {
    const customer = {
      type: 'CD',
      status: 'new',
    }

    if (app.quoteCriteria && app.quoteCriteria.leadPassenger) {
      customer.email = app.quoteCriteria.leadPassenger.emailAddress

      customer.location = {
        country: app.quoteCriteria.leadPassenger.address.country,
        province: app.quoteCriteria.leadPassenger.address.region,
        town: app.quoteCriteria.leadPassenger.address.city,
      }
    }

    return customer
  }

  if (app.user.isTravelAgent) {
    return snapshotTravelAgent(app)
  } else {
    return snapshotCustomer(app)
  }
}

function snapshotPlatform (app) {
  return {
    id: 'BookingServices',
    brand_code: app.config.sellingCompanyCode.substring(0, 2),
    agent_ui: app.user.isTravelAgent,
    testing: app.config.environment !== 'production',
    regional_domain_prefix: constants.regionalDomains[app.config.sellingCompanyCode.substring(2)],
    sales_geo_region: app.config.sellingCompanyCode.substring(2),
    company_code: app.config.sellingCompanyCode,
    version: app.experienceVersion,
  }
}

function snapshotScreen (app) {
  const screenName = app.currentTabId
  return {
    name: screenName,
    step_number: `${tabStepNumberMap[screenName]}`,
    confirmation_page: 'confirmation' == screenName ? 'true' : 'false',
  }
}

function snapshotTravelProtection (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.passengers)
    ? priceBreakdown.passengers
      .filter(passenger => passenger.travelProtection)
      .map(passenger => passenger.travelProtection === TravelProtectionPlanType.standard
        ? passenger.travelProtectionPlan
        : passenger.travelProtectionPlanPlus
      )
    : []
}

function snapshotExtraDaysBefore (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.extraStays && priceBreakdown.extraStays.before)? priceBreakdown.extraStays.before : {nights:0}
}

function snapshotExtraDaysAfter (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.extraStays && priceBreakdown.extraStays.after )? priceBreakdown.extraStays.after  : {nights:0}
}

function snapshotTransfers (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.transfers)?priceBreakdown.transfers:[]
}

function snapshotPrepaidGratuities (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.passengers)
    ?priceBreakdown.passengers
      .filter(p => p.requestedPrepaidGratuities)
      .map(p => p.requestedPrepaidGratuities)
    :[]
}

function snapshotParty (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.rooms) ? {
    size: priceBreakdown.rooms.reduce( (accumulator, room)=>{ return accumulator + room.passengers.length }, 0 ),
    value: priceBreakdown.totalBeforeDiscount,
  } : {}
}

function snapshotPassengers (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.passengers)? priceBreakdown.passengers: []
}

function snapshotTourProducts (priceBreakdown, quoteCriteria) {
  let tourProduct = {}
  if (priceBreakdown && priceBreakdown.products) {
    tourProduct = priceBreakdown.products.filter(product=>product.typeCode==='MC')[0]
  }
  if (priceBreakdown && priceBreakdown.rooms) {
    tourProduct.variant = priceBreakdown.rooms.map(rooms => rooms.roomType).join(',')

    const costToReport = priceBreakdown.rooms
      .reduce( (accumulator, room)=>{
        accumulator.push(...room.passengers)
        return accumulator
      }, [])
      .reduce((accumulator, passenger)=>{return accumulator + passenger.roomPrice}, 0)

    tourProduct.costToReport = costToReport
  }

  if (quoteCriteria) {
    const {
      apiCodes,
      endDate,
      id,
      name,
      productCode,
      startDate,
      tourName,
      shortTourName,
      variantName,
      definiteDeparture,
    } = quoteCriteria.departure || {}
    const {tourId, tourOptionId} = apiCodes || {}
    Object.assign(tourProduct, {
      endDate: endDate.toString(),
      departureId: id,
      name,
      productCode,
      startDate: startDate.toString(),
      tourId,
      tourOptionId,
      tourName,
      shortTourName,
      variantName,
      definiteDeparture,
      tourDuration: startDate.differenceInDays(endDate) + 1,
    })
  }

  return tourProduct
}

function snapshotDiscounts (priceBreakdown) {
  return (priceBreakdown && priceBreakdown.discounts)?priceBreakdown.discounts:[]
}

function snapshotCart (priceBreakdown, quoteCriteria, cartId) {
  return {
    transfers: snapshotTransfers(priceBreakdown),
    travelProtection: snapshotTravelProtection(priceBreakdown),
    extraStaysBefore: snapshotExtraDaysBefore(priceBreakdown),
    extraStaysAfter: snapshotExtraDaysAfter(priceBreakdown),
    prepaidGratuities: snapshotPrepaidGratuities(priceBreakdown),
    party: snapshotParty(priceBreakdown), //TODO why 'party'+'passengers'? something out of line in here..
    passengers: snapshotPassengers(priceBreakdown),
    discounts: snapshotDiscounts(priceBreakdown),
    tourProduct: snapshotTourProducts(priceBreakdown, quoteCriteria), //TODO would really like not to have ot use quoteCriteria but nothing else has tour name...
    cartId: cartId,
  }
}

export default (app, cartId)=>{
  const priceBreakdown = app.quoteCriteria ? app.quoteCriteria.priceBreakdown() : undefined

  const snapshot = {
    user: snapshotUser(app),
    platform: snapshotPlatform(app),
    gte: app.config.gte,
    screen: snapshotScreen(app),
    cart: snapshotCart(priceBreakdown, app.quoteCriteria, cartId),
    tab: app.currentTabId,
    priceBreakdown: priceBreakdown,
    booking: app.booking,
    externalRef: app.quoteCriteria ? app.quoteCriteria.ExternalRef : undefined,
  }

  return snapshot
}
